import { useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

export function marketCatalogQuery(country = "US", department = "", sort = "", category = "", sub = "", store = "", brand = "") {
  return {
    queryKey: ["market-storefront", country || "US", department || "", category || "", sub || "", store || "", brand || "", sort || "featured"],
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = { country: country || "US", limit: 48 };
      if (department) params.department = department;
      if (category) params.category = category;
      if (sub) params.sub = sub;
      if (store) params.store = store;
      if (brand) params.brand = brand;
      if (sort === "price_asc" || sort === "price_desc") params.sort = sort;
      return (await api.get("/shop/market", { params })).data;
    },
  };
}

function mergeProducts(current, incoming) {
  const seen = new Set(current.map((product) => product.id));
  return [...current, ...(incoming || []).filter((product) => product?.id && !seen.has(product.id))];
}

async function nextPage({ country, department, offset, sort = "", category = "", sub = "", store = "", brand = "" }) {
  const params = { country: country || "US", offset, limit: 48 };
      if (department) params.department = department;
      if (category) params.category = category;
  if (sub) params.sub = sub;
  if (store) params.store = store;
  if (brand) params.brand = brand;
  if (sort === "price_asc" || sort === "price_desc") params.sort = sort;
  return (await api.get("/shop/market", { params })).data;
}

export default function MarketStorefront({ country = "US", department = "", sort = "", category = "", sub = "", store = "", brand = "" }) {
  const { data, isLoading, isError, isPlaceholderData } = useQuery(
    marketCatalogQuery(country, department, sort, category, sub, store, brand),
  );
  const [extra, setExtra] = useState([]);
  const [nextOffset, setNextOffset] = useState(data?.next_offset ?? null);
  const [loadingMore, setLoadingMore] = useState(false);
  const anchor = data?.products?.[0]?.id || "";

  useEffect(() => {
    setExtra([]);
    setNextOffset(data?.next_offset ?? null);
  }, [country, department, category, sub, store, brand, sort, data?.total, anchor, data?.next_offset]);

  if (isLoading || isPlaceholderData) {
    return (
      <div data-testid="market-storefront-loading" className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[3/4] shimmer" />)}
      </div>
    );
  }

  if (isError || !data) {
    return <p className="py-16 text-center text-aura-muted">This shop could not load. Try again in a moment.</p>;
  }

  const products = mergeProducts(data.products || [], extra);
  const loadMore = async () => {
    if (nextOffset == null || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await nextPage({ country, department, offset: nextOffset, sort, category, sub, store, brand });
      setExtra((prev) => mergeProducts(prev, page.products));
      setNextOffset(page.next_offset ?? null);
    } finally {
      setLoadingMore(false);
    }
  };

  const total = Number(data.total || 0);
  return (
    <section data-testid="market-storefront">
      {total > 0 ? (
        <p className="mb-4 text-sm text-aura-muted" data-testid="market-count">
          Showing {products.length.toLocaleString()} of {total.toLocaleString()}
        </p>
      ) : null}
      <div className="grid grid-cols-2 items-stretch gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {products.map((product, index) => (
          <ProductCard
            key={product.id}
            index={index}
            instant
            product={{
              ...product,
              images: product.image ? [product.image] : [],
              seller_name: product.store || product.brand || "",
              external: product.source !== "aura",
            }}
          />
        ))}
      </div>
      {products.length === 0 ? (
        <p className="py-16 text-center text-aura-muted">Nothing in this department yet.</p>
      ) : null}
      {nextOffset != null ? (
        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            data-testid="market-load-more"
            className="text-sm tracking-luxe uppercase border border-aura-border px-8 py-3 hover:border-aura-accent disabled:opacity-50"
          >
            {loadingMore ? "Loading…" : `Load more · ${(total - products.length).toLocaleString()} left`}
          </button>
        </div>
      ) : null}
    </section>
  );
}
